import { desktopBruecke } from '../platform/gleis';
import type { Store, Where } from './port';
import type { TableName } from './rows';
import { AUFSTIEGE } from './schema';
import { buildInsert, buildWhere } from './sqlite';

/**
 * Die Ablage am Desktop — derselbe `Store`-Vertrag, dahinter echtes SQLite.
 *
 * ⭐ **Derselbe SQL wie am iPad.** `buildInsert` und `buildWhere` kommen aus `sqlite.ts`,
 * nicht als Zweitschrift: das echte Upsert (`ON CONFLICT(id) DO UPDATE`) statt
 * `INSERT OR REPLACE` verhindert, dass ein blosses „Exam-Namen ändern" über die Kaskaden die
 * halbe Prüfungsstruktur löscht. Eine zweite Fassung dieser Regel wäre die Sorte Fehler, die
 * erst im Prüfungsbetrieb auffällt.
 *
 * ⭐ **Dieselbe Aufstiegskette.** `AUFSTIEGE` aus `schema.ts` geht beim Öffnen über die
 * Brücke; drüben zählt `PRAGMA user_version` mit (`desktop/ablage.cts`). Zwei Schemata wären
 * zwei Wahrheiten.
 *
 * ⚠️ **Die Datei liegt bei den Nutzerdaten, nicht bei den Belegen.** `~/Documents` wird von
 * iCloud (macOS) und oft von OneDrive (Windows) synchronisiert; eine laufende SQLite-Datei
 * dort ist ein bekannter Weg zu einer beschädigten Ablage. Wo genau, entscheidet
 * `desktop/main.cts::ablagePfad`.
 *
 * ⬅️ **Keine Übernahme aus dem Browserzweig.** Wer am Rechner bisher im Browser gearbeitet
 * hat, dessen IndexedDB bleibt liegen; diese Ablage startet leer. Eine Übernahme wäre eine
 * eigene Etappe und keine Nebenwirkung.
 */

/** Was SQLite an Werten binden kann. */
type Wert = string | number | null;

function rohr() {
  const bruecke = desktopBruecke();
  if (!bruecke) throw new Error('Kein Desktop-Gleis — die Brücke fehlt.');
  return bruecke.ablage;
}

export function createDesktopStore(): Store {
  /**
   * Wächter gegen Verschachtelung — wie im Geräte- und im Browseradapter.
   *
   * ⚠️ **Er steht hier und nicht im Hauptprozess.** Dort wäre er blind: `tx(fn)` führt `fn`
   * im Renderer aus, zwischen `beginne()` und `schreibeFest()` liegen also fremde Aufrufe.
   * Der Zustand, der über „verschachtelt oder nicht" entscheidet, lebt deshalb auf dieser
   * Seite der Brücke.
   */
  let inTx = false;

  const tx = async <T>(fn: (t: Store) => Promise<T>): Promise<T> => {
    if (inTx) {
      throw new Error('tx() ist nicht verschachtelbar — eine Transaktion läuft bereits');
    }
    inTx = true;
    try {
      await rohr().beginne();
      try {
        const ergebnis = await fn(ablage);
        await rohr().schreibeFest();
        return ergebnis;
      } catch (fehler) {
        try {
          await rohr().nimmZurueck();
        } catch (rollbackFehler) {
          // Wie in `sqlite.ts` und `web.ts`: der ursprüngliche Fehler bleibt massgeblich, das
          // gescheiterte Zurücknehmen hängt nur als Begleitinformation daran.
          if (typeof fehler === 'object' && fehler !== null) {
            (fehler as { restoreErrors?: unknown[] }).restoreErrors = [rollbackFehler];
          }
        }
        throw fehler;
      }
    } finally {
      inTx = false;
    }
  };

  const stapelSchreiben = async (table: TableName, rows: Record<string, unknown>[]) => {
    for (const satz of rows) {
      const { sql, werte } = buildInsert(table, satz);
      await rohr().ausfuehren(sql, werte as Wert[]);
    }
  };

  const ablage: Store = {
    async open() {
      const stand = await rohr().oeffne(AUFSTIEGE.map((s) => ({ bis: s.bis, saetze: [...s.saetze] })));
      // Der Aufstieg ist nichts Alltägliches: er läuft einmal je Fassung und entscheidet, ob
      // die App überhaupt mit ihrer Ablage zusammenpasst. Deshalb steht er in der Konsole.
      if (stand.gestiegenVon !== stand.fassung) {
        console.info(
          '[Ablage] Aufstieg von Fassung ' + stand.gestiegenVon + ' auf ' + stand.fassung,
        );
      }
    },

    async put(table, rows) {
      // Alles oder nichts je Aufruf, wie in den anderen beiden Adaptern: läuft schon eine
      // Transaktion, wird sie mitbenutzt; sonst spannt `put` selbst eine auf, damit ein
      // Abbruch mitten im Stapel keine halb geschriebenen Zeilen hinterlässt.
      const stapel = rows as unknown as Record<string, unknown>[];
      if (inTx) await stapelSchreiben(table, stapel);
      else await tx(() => stapelSchreiben(table, stapel));
    },

    async all<T>(table: TableName, where?: Where) {
      const { sql, werte } = buildWhere(where);
      const zeilen = await rohr().abfragen('SELECT * FROM ' + table + sql, werte as Wert[]);
      return zeilen as T[];
    },

    async remove(table, where) {
      const { sql, werte } = buildWhere(where);
      await rohr().ausfuehren('DELETE FROM ' + table + sql, werte as Wert[]);
    },

    tx,

    async close() {
      await rohr().schliesse();
      inTx = false;
    },
  };
  return ablage;
}
